import { uniqueKey } from 'global/common/keys'
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext'
import { gameScoringTypes } from 'projectSpecific/sports/cnr/reducers/SportsSeasonReducer'
import React, { useContext, useMemo } from 'react'
import { Icon, Label } from 'semantic-ui-react'
import { matchFilterHelpers } from '../matches/MatchFilters'
import MotionDiv, { FadeWrapper } from 'global/motions/MotionDiv'
import { ParentContext } from 'global/cnr/contexts/ParentContext'
import { CreatePlayoffContext } from 'projectSpecific/sports/create/CreatePlayoff'
import { useTeamRecords } from 'projectSpecific/sports/useHelpers/useTeamRecords'
import { _standingsViewTypes } from './SectionStandingz'
import { createInitials } from 'global/common/dataAdjust'

const _tris = {
  l: 'l',
  mr: 'mr',
  n: 'n',
  nsl: 'nsl',
  nst: 'nst',
  nsw: 'nsw',
  op: 'op',
  p: 'p',
  sl: 'sl',
  smr: 'smr',
  sp: 'sp',
  st: 'st',
  sw: 'sw',
  t: 't',
  w: 'w',
}

export const _teamNames = ['Catholic', 'Christian', 'Academy', 'County', 'McMillan', 'Bradock Hills', 'Township']
export const _teamNamesReplace = ['Cath.', 'Christ.', 'Acad.', 'Co.', 'Mac', 'BH', 'Twp.']

export const TeamSectionRecord = ({
  forPlayoff,
  handleTeamClick,
  isNonSection,
  rowKey,
  standingsViewType,
  tableType,
  team_favs,
  team,
  valueW,
}) => {

  const parentContext = useContext(ParentContext);
  const { fns } = parentContext ?? {}
  const { navigation_fns } = fns ?? {}

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { currentSeason_info, sportsSeason_info, sportSettings_info, matches_info } = sportsSeason_state ?? {}
  const { showRankingsInStandings } = sportSettings_info ?? {}
  const { showMatchesRemainingCount } = sportsSeason_info ?? {}
  const { gameScoringType, gameAllowTies, _sportsModeSport } = currentSeason_info ?? {}

  const createPlayoffContext = useContext(CreatePlayoffContext);
  const { createPlayoff_state } = createPlayoffContext ?? {}
  const { playoff_info } = createPlayoff_state ?? {}
  const { playoffLevel, playoffLevelTeams } = playoff_info ?? {}

  const teams_playoffGroup = playoffLevelTeams?.[playoffLevel]
  const tpgs = teams_playoffGroup ? _.size(teams_playoffGroup) : 0

  const { matches, matches_playoffs } = matches_info ?? {}

  const handleMatchClick = (e, teamMatch) => {
    e.stopPropagation()
    navigation_fns.handleNav_item({ key: 'matches' }, teamMatch._itemKey)
  }

  let cellCount = 0

  const label_currentRank = (currentRank) => <Label size={'mini'} className={'team-rank'} circular>{currentRank}</Label>
  const label_section = (sectionRank) => <Label size='mini'>{sectionRank}</Label>
  const label_matchesRemaining = (smr, mr) => <Label size='mini' className='sssmr' title={'Remaining (section:all)'}>
    <Icon name='caret right' />{smr}{':'}{mr}
  </Label>

  const icon_fav = () => <Icon className={'ssfav'} name={'star'} color={'blue'} size={'tiny'} />

  const recordDiv = (t, value, width) => {
    cellCount++
    return <div className={`trr-${t}`} key={uniqueKey('ss', 'rc', cellCount)} >{value}</div>
  }

  const lbls_form = (team, teamMatches) => {
    const lbls = _.map(teamMatches, teamMatch => {
      const { display, winner, isSectionMatch, scoreFull, opponentName } = teamMatch
      let color = winner ? 'green' : 'red'
      let title = scoreFull;
      return <Label
        className={isSectionMatch ? 'sm' : ''}
        title={title}
        size='mini'
        color={color}
        onClick={(e) => handleMatchClick(e, teamMatch)}>
        {`${opponentName} ${display}`}
      </Label>
    }).reverse()
    return <div className='trs form'>{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_remaining = (team, teamMatches) => {
    const lbls = _.map(teamMatches, teamMatch => {
      const { display, homeMatch, isSectionMatch, prevResult } = teamMatch
      let color = 'grey'
      let cn = isSectionMatch ? 'sm' : null
      let title;
      if (prevResult) {
        cn += prevResult.winner ? ' pw' : ' pl'
        title = prevResult?.scoreFull
      } else {
        cn += ' pn'
      }
      return <Label
        className={cn}
        title={title}
        size='mini'
        color={color}
        onClick={(e) => handleMatchClick(e, teamMatch)}>
        {display}
      </Label>
    }).reverse()
    return <div className='trs remaining' >{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_insights = (team) => {
    const lbls = []
    const color = 'grey'
    const size = 'mini'
    const { elo, performanceDelta, strengthOfSchedule, winsVsHigherRated } = team?.record?.eloInfo
    lbls.push(<Label title={'ELO'} color={elo >= 1500 ? 'green' : 'red'} size={size}>{`ELO: ${elo}`}</Label>)
    lbls.push(<Label title={'Performance Delta'} color={performanceDelta > 0 ? 'green' : 'red'} size={size}>{`PD: ${performanceDelta}`}</Label>)
    lbls.push(<Label title={'Strength of Schedule'} color={color} size={size}>{`SOS: ${strengthOfSchedule}`}</Label>)
    lbls.push(<Label title={'Wins vs Higher Rated'} color={winsVsHigherRated > 0 ? 'green' : 'red'} size={size}>{`WVHR: ${winsVsHigherRated}`}</Label>)
    return <div className='trs insights' >{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_bestWins = (team) => {
    const lbls = []
    const { elo, bestWins } = team?.record?.eloInfo
    if (bestWins) {
      _.forEach(bestWins, w => {
        lbls.push(<Label color='green' size='mini'>{`${createInitials(w.name, 3)} ${w.result}`}</Label>)
      })
    }
    return <div className='trs bestWins'>{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_worstLosses = (team) => {
    const lbls = []
    const { worstLosses } = team?.record?.eloInfo
    if (worstLosses) {
      _.forEach(worstLosses, w => {
        lbls.push(<Label color='red' size='mini'>{`${createInitials(w.name, 3)} ${w.result}`}</Label>)
      })
    }
    return <div className='trs worstLosses' >{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_vs = (team) => {
    const lbls = []
    const vsInfo = team?.record?.vsInfo
    const { vs } = vsInfo ?? {}
    if (vs) {
      _.forEach(vs, (v, vk) => {
        const _vk = vk.split('_')
        const l = _vk[0]
        const s = _vk[1].replace('section', '')
        lbls.push(<Label color={v.winPct > 0 ? 'green' : 'red'} size='mini'>{`${l}-${s} #${v.games}/${v.winPct}%`}</Label>)
      })
    }
    return <div className='trs bestWins' >{_.isEmpty(lbls) ? 'None' : lbls}</div>
  }

  const lbls_nonSection = (team) => {
    const lbls = []
    const { worstLosses } = team?.record?.eloInfo
    if (worstLosses) {
      _.forEach(worstLosses, w => {
        lbls.push(<Label color='red' size='mini'>{`${createInitials(w.name, 3)} ${w.result}`}</Label>)
      })
    }
    return <div className='trs worstLosses' >{lbls}</div>
  }

  const teamDiv_form = () => {
    const tlm = matchFilterHelpers.getLastMatches(matches, team, 5)
    return lbls_form(team, tlm)
  }

  const teamDiv_remaining = () => {
    const tlm = matchFilterHelpers.getRemainigMatches(matches, team, 5)
    return lbls_remaining(team, tlm)
  }

  const teamDiv_vs = () => lbls_vs(team)

  const teamDiv_ns = () => {
    const tlm = matchFilterHelpers.getNonSectionMatches(matches, team)
    console.log('tlm', tlm)
    return lbls_form(team, tlm)
  }

  const teamDiv_bestWins = () => lbls_bestWins(team)
  const teamDiv_worstLosses = () => lbls_worstLosses(team)

  const teamDiv_insights = () => {
    return lbls_insights(team)
  }

  const teamRecordDiv = (record) => {

    let trs = []

    const cells = []

    switch (gameScoringType) {
      case gameScoringTypes.match:
      case gameScoringTypes.matchPoints:
      case gameScoringTypes.inning:
        if (record) {
          if (isNonSection) {
            trs = [_tris.sw, _tris.sl]
          } else {
            if (tableType === 'normal') {
              trs = [_tris.nsw, _tris.nsl]
            } else {
              trs = [_tris.sw, _tris.sl]
            }
            if (gameScoringType === gameScoringTypes.matchPoints) {
              trs.push(_tris.p)
            }
            trs.push(_tris.w)
            trs.push(_tris.l)
            if (gameScoringType === gameScoringTypes.matchPoints) {
              trs.push(_tris.op)
            }
          }
        } else {
          trs = [_tris.n, _tris.n]
          if (!isNonSection) {
            if (tableType === 'normal') {
              trs.push(_tris.n)
              trs.push(_tris.n)
            } else {
              trs.push(_tris.n)
              trs.push(_tris.n)
            }
          }
        }
        break;

      case gameScoringTypes.half:
        if (record) {
          if (isNonSection) {
            trs = [_tris.sw, _tris.sl, _tris.st]
          } else {
            if (tableType === 'normal') {
              trs = [_tris.nsw, _tris.nsl, _tris.nst, _tris.nsp]
            } else {
              trs = [_tris.sw, _tris.sl, _tris.st, _tris.sp]
            }
            trs.push(_tris.w)
            trs.push(_tris.l)
            trs.push(_tris.t)
            trs.push(_tris.op)
          }
        } else {
          trs = [_tris.n, _tris.n]
          if (!isNonSection) {
            if (tableType === 'normal') {
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
            } else {
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
            }
          }
        }
        break;

      case gameScoringTypes.quarter:
        if (record) {
          if (isNonSection) {
            trs = [_tris.sw, _tris.sl, _tris.st]
          } else {
            if (tableType === 'normal') {
              trs = [_tris.nsw, _tris.nsl, _tris.nst]
            } else {
              if (gameAllowTies) {
                trs = [_tris.sw, _tris.sl, _tris.st]
              } else {
                trs = [_tris.sw, _tris.sl]
              }
            }
            trs.push(_tris.w)
            trs.push(_tris.l)
            gameAllowTies && trs.push(_tris.t)
          }
        } else {
          trs = [_tris.n, _tris.n]
          if (!isNonSection) {
            if (tableType === 'normal') {
              trs.push(_tris.n)
              trs.push(_tris.n)
              gameAllowTies && trs.push(_tris.n)
            } else {
              trs.push(_tris.n)
              trs.push(_tris.n)
              gameAllowTies && trs.push(_tris.n)
            }
          }
        }
        break;

      default:
        if (record) {
          if (isNonSection) {
            trs = [_tris.sw, _tris.sl, _tris.st]
          } else {
            if (tableType === 'normal') {
              trs = [_tris.nsw, _tris.nsl, _tris.nst, _tris.nsp]
            } else {
              trs = [_tris.sw, _tris.sl, _tris.st, _tris.sp]
            }
            trs.push(_tris.w)
            trs.push(_tris.l)
            trs.push(_tris.t)
            trs.push(_tris.op)
          }
        } else {
          trs = [_tris.n, _tris.n]
          if (!isNonSection) {
            if (tableType === 'normal') {
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
            } else {
              trs.push(_tris.n)
              trs.push(_tris.n)
              trs.push(_tris.n)
            }
          }
        }
        break;
    }

    trs.forEach(t => {
      switch (t) {
        case _tris.n:
          cells.push(recordDiv(t, 0, valueW))
          break;
        default:
          cells.push(recordDiv(t, record[t], valueW))
      }

    })

    return cells
  }

  const content_row = (record) => {
    switch (standingsViewType) {
      case _standingsViewTypes.bestWins:
      case _standingsViewTypes.form:
      case _standingsViewTypes.full:
      case _standingsViewTypes.insights:
      case _standingsViewTypes.remaining:
      case _standingsViewTypes.worstLosses:
      case _standingsViewTypes.vs:
      case _standingsViewTypes.nonSection:
        return teamRecordDiv(record)
    }
  }

  const standingsTeamRow = useMemo(() => {

    const { name, record, _itemKey, currentRank, teamNumber } = team ?? {}
    const { display, playoffTeam, nonPlayoffTeam, playoffEliminated, playoffAssured, sectionRank, smr, mr, tmm, _inactive } = record ?? {}

    const playoffTeamCompare = false // playoffTeamCompares && team ? _.find(playoffTeamCompares, { id: team._itemKey }) : null

    const _name = teamName(name)

    const isFav = team_favs && team_favs[_itemKey] ? true : false
    let _rowCn = 'standing-team smr'
    _rowCn += isFav ? ' fav' : ''
    _rowCn += gameScoringType === gameScoringTypes.matchPoints ? ' pts' : ''

    if (playoffTeamCompare) { _rowCn += ' ptc' }

    if (isNonSection && 1 === 3) {
      return <div key={uniqueKey('ss', 'sr', rowKey)} className={_rowCn}>
        <div>{index + 1}</div>
        <div>{'#' + teamNumber}</div>
        <div>{_name}</div>
      </div>
    } else {

      // adjust the class for the row
      if (playoffTeam) { _rowCn += ' ss-pt' }
      if (nonPlayoffTeam) { _rowCn += ' ss-npt' }
      if (playoffEliminated) { _rowCn += ' ss-npte' }
      if (playoffAssured) { _rowCn += ' ss-npta' }
      if (tmm && tmm > 0) { _rowCn += ' ss-mm' }
      if (_inactive) { _rowCn += ' t-mia' }
      if (standingsViewType) { _rowCn += ' svt-full' }

      const _playoffTeam = teams_playoffGroup ? _.find(teams_playoffGroup, { _itemKey }) : null
      if (_playoffTeam) { _rowCn += ' ptm' }

      // trr-team and trs remaining are sub divs
      return <div key={uniqueKey('ss', 'sr', rowKey)} className={_rowCn} onClick={(e) => handleTeamClick(e, team)}>
        <div className={`trr-team`}>
          <Label size='mini'>{sectionRank}</Label>
          {_name}
          {/* {content_display(display)} */}
          {isFav && icon_fav()}
          {showRankingsInStandings && currentRank && label_currentRank(currentRank)}
          {!forPlayoff && (mr > 0) && showMatchesRemainingCount > smr && label_matchesRemaining(smr, mr)}
          {forPlayoff && label_section(sectionRank)}
        </div>
        {content_row(record)}
      </div>
    }
  }, [standingsViewType, gameScoringType, team?.record])

  const additionalTeamRow = useMemo(() => {
    switch (standingsViewType) {
      case _standingsViewTypes.bestWins:
        return teamDiv_bestWins()
      case _standingsViewTypes.worstLosses:
        return teamDiv_worstLosses()
      case _standingsViewTypes.form:
        return teamDiv_form()
      case _standingsViewTypes.insights:
        return teamDiv_insights()
      case _standingsViewTypes.remaining:
        return teamDiv_remaining()
      case _standingsViewTypes.vs:
        return teamDiv_vs()
      case _standingsViewTypes.nonSection:
        return teamDiv_ns()
    }
  })

  return <FadeWrapper>
    {standingsTeamRow}
    {additionalTeamRow}
  </FadeWrapper>

}

const teamName = (name) => {
  if (!name) return name;
  let _name = name
  for (let i = 0; i < _teamNames.length; i++) {
    if (name.includes(_teamNames[i])) {
      _name = name.replace(_teamNames[i], _teamNamesReplace[i]);
    }
  }
  return _name
}
